import React, { useRef, useState } from 'react';
import { useBusinessPlan } from '../../App';
import { useCurrency } from '../../contexts/CurrencyContext';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Download, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Component Exporter - Fetches section data and generates professional PDF
 * Hybrid Approach: Backend data + Professional formatting
 */
const ComponentExporter = ({ selectedSections, planName, onComplete }) => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency, displayCurrency } = useCurrency();
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [progress, setProgress] = useState('');

  const getSectionName = (sectionId) => {
    const nameMap = {
      'strategic_framework': 'Strategic Framework',
      'cost_centers': 'Cost Center Management',
      'cost_management': 'Cost Management',
      'financial_management': 'Financial Management',
      'financial_planning': 'Financial Planning',
      'financial_calculations': 'Financial Calculations',
      'financial_statements': 'Financial Statements',
      'financial_analytics': 'Financial Analytics',
      'planning_dashboard': 'Planning Dashboard',
      'scenario_planning': 'Strategic Scenario Planning'
    };
    return nameMap[sectionId] || sectionId;
  };

  /**
   * Parse selected sections into structured format
   * Returns: { section_id: [subsection_ids...] }
   */
  const parseSelectedSections = () => {
    const structured = {};
    
    selectedSections.forEach(selection => {
      const [mainSection, subSection] = selection.split('.');
      if (!structured[mainSection]) {
        structured[mainSection] = [];
      }
      if (subSection && !structured[mainSection].includes(subSection)) {
        structured[mainSection].push(subSection);
      }
    });
    
    return structured;
  };

  /**
   * Fetch data for a specific section
   */
  const fetchSectionData = async (sectionId) => {
    const endpointMap = {
      'strategic_framework': `${API}/plan-data/strategic-framework?plan_id=${currentPlan.id}`,
      'sales_analytics': `${API}/plan-data/sales-analytics?plan_id=${currentPlan.id}`,
      'cost_centers': `${API}/plan-data/cost-centers?plan_id=${currentPlan.id}`,
      'cost_management': `${API}/plan-data/cost-management?plan_id=${currentPlan.id}`,
      'financial_management': `${API}/plan-data/financial-management?plan_id=${currentPlan.id}`,
      'financial_statements': `${API}/plan-data/financial-statements?plan_id=${currentPlan.id}`,
      'financial_planning': `${API}/plan-data/financial-planning?plan_id=${currentPlan.id}`,
      'financial_analytics': `${API}/plan-data/financial-analytics?plan_id=${currentPlan.id}`,
      'planning_dashboard': `${API}/plan-data/planning-dashboard?plan_id=${currentPlan.id}`,
      'scenario_planning': `${API}/plan-data/scenario-planning?plan_id=${currentPlan.id}`
    };

    const endpoint = endpointMap[sectionId];
    if (!endpoint) return null;

    try {
      const response = await axios.get(endpoint);
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${sectionId}:`, error);
      return null;
    }
  };

  /**
   * Format currency for PDF - uses display currency from context
   */
  const formatCurrencyForPDF = (amount, baseCurrency = null) => {
    if (!amount && amount !== 0) return formatCurrency(0, baseCurrency || currentPlan?.currency || 'USD');
    return formatCurrency(amount, baseCurrency || currentPlan?.currency || 'USD');
  };

  /**
   * Add Strategic Framework - Objectives sub-section
   */
  const addSFObjectivesSubSection = (pdf, data, yPos) => {
    // Check if we need a new page
    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }

    pdf.setFontSize(14);
    pdf.setTextColor(31, 41, 55);
    pdf.text('Strategic Objectives', 20, yPos);
    yPos += 8;

    const objectives = data.objectives || [];
    
    if (objectives.length === 0) {
      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      pdf.text('No objectives defined yet', 20, yPos);
      yPos += 10;
      return yPos;
    }

    // Objectives summary
    pdf.setFontSize(10);
    pdf.setTextColor(75, 85, 99);
    pdf.text(`Total Objectives: ${objectives.length}`, 20, yPos);
    yPos += 10;

    // Objectives table with detailed information
    const objectivesData = objectives.map(obj => {
      // Handle both V1 and V2 field names
      const name = obj.objective_name || obj.name || 'Unnamed';
      const pillar = obj.pillar || obj.category || '-';
      const owner = obj.owner || '-';
      const status = obj.status || 'Not Set';
      const startDate = obj.start_date ? new Date(obj.start_date).toLocaleDateString() : '-';
      const endDate = obj.end_date || obj.target_date ? new Date(obj.end_date || obj.target_date).toLocaleDateString() : '-';
      
      return [name, pillar, owner, status, startDate, endDate];
    });

    autoTable(pdf, {
      startY: yPos,
      head: [['Objective', 'Strategic Pillar', 'Owner', 'Status', 'Start Date', 'Target Date']],
      body: objectivesData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 20, right: 20 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 35 },
        2: { cellWidth: 30 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 }
      }
    });

    yPos = pdf.lastAutoTable.finalY + 12;
    return yPos;
  };

  /**
   * Add Strategic Framework - Initiatives sub-section
   */
  const addSFInitiativesSubSection = (pdf, data, yPos) => {
    // Check if we need a new page
    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }

    pdf.setFontSize(14);
    pdf.setTextColor(31, 41, 55);
    pdf.text('Strategic Initiatives', 20, yPos);
    yPos += 8;

    const initiatives = data.initiatives || [];
    
    if (initiatives.length === 0) {
      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      pdf.text('No initiatives defined yet', 20, yPos);
      yPos += 10;
      return yPos;
    }

    // Initiatives summary
    const totalBudget = initiatives.reduce((sum, init) => sum + (init.budget || 0), 0);
    pdf.setFontSize(10);
    pdf.setTextColor(75, 85, 99);
    pdf.text(`Total Initiatives: ${initiatives.length}`, 20, yPos);
    yPos += 6;
    pdf.text(`Total Budget: ${formatCurrencyForPDF(totalBudget)}`, 20, yPos);
    yPos += 10;

    // Initiatives table
    const initiativesData = initiatives.map(init => {
      // Handle both V1 and V2 field names
      const name = init.initiative_name || init.name || 'Unnamed';
      const linkedObjective = init.linked_objective_name || init.objective_id || init.linked_objective_id || '-';
      const budget = formatCurrencyForPDF(init.budget || 0);
      const status = init.status || 'Not Set';
      const startDate = init.timeline_start || init.start_date ? new Date(init.timeline_start || init.start_date).toLocaleDateString() : '-';
      const endDate = init.timeline_end || init.end_date ? new Date(init.timeline_end || init.end_date).toLocaleDateString() : '-';
      
      return [name, linkedObjective, budget, status, startDate, endDate];
    });

    autoTable(pdf, {
      startY: yPos,
      head: [['Initiative', 'Linked Objective', 'Budget', 'Status', 'Start Date', 'End Date']],
      body: initiativesData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 20, right: 20 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 40 },
        2: { cellWidth: 30 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 }
      }
    });

    yPos = pdf.lastAutoTable.finalY + 12;
    return yPos;
  };

  /**
   * Add Strategic Framework - Revenue Streams sub-section
   */
  const addSFRevenueStreamsSubSection = (pdf, data, yPos) => {
    // Check if we need a new page
    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }

    pdf.setFontSize(14);
    pdf.setTextColor(31, 41, 55);
    pdf.text('Revenue Streams', 20, yPos);
    yPos += 8;

    const revenueStreams = data.revenue_streams || [];
    
    if (revenueStreams.length === 0) {
      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      pdf.text('No revenue streams defined yet', 20, yPos);
      yPos += 10;
      return yPos;
    }

    // Revenue streams summary
    pdf.setFontSize(10);
    pdf.setTextColor(75, 85, 99);
    pdf.text(`Total Revenue Streams: ${revenueStreams.length}`, 20, yPos);
    yPos += 10;

    // Revenue streams table
    const streamsData = revenueStreams.map(stream => {
      // Handle both V1 and V2 field names
      const name = stream.stream_name || stream.name || 'Unnamed';
      const type = stream.stream_type || stream.type || '-';
      const linkedInitiative = stream.linked_initiative_name || stream.initiative_id || stream.linked_initiative_id || '-';
      const description = stream.description || stream.notes || '-';
      
      return [name, type, linkedInitiative, description];
    });

    autoTable(pdf, {
      startY: yPos,
      head: [['Revenue Stream', 'Type', 'Linked Initiative', 'Description']],
      body: streamsData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 20, right: 20 },
      columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 35 },
        2: { cellWidth: 50 },
        3: { cellWidth: 60 }
      }
    });

    yPos = pdf.lastAutoTable.finalY + 12;
    return yPos;
  };

  /**
   * Add Strategic Framework - Products & Services sub-section
   */
  const addSFProductsSubSection = (pdf, data, yPos) => {
    // Check if we need a new page
    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }

    pdf.setFontSize(14);
    pdf.setTextColor(31, 41, 55);
    pdf.text('Products & Services', 20, yPos);
    yPos += 8;

    const products = data.products || [];
    
    if (products.length === 0) {
      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      pdf.text('No products or services defined yet', 20, yPos);
      yPos += 10;
      return yPos;
    }

    // Products summary
    pdf.setFontSize(10);
    pdf.setTextColor(75, 85, 99);
    pdf.text(`Total Products/Services: ${products.length}`, 20, yPos);
    yPos += 10;

    // Products table with linked revenue stream
    const productsData = products.map(product => {
      // Handle both V1 and V2 field names
      const name = product.name || product.product_name || 'Unnamed';
      const type = product.type || product.product_type || '-';
      const sku = product.sku || product.product_code || '-';
      const price = formatCurrencyForPDF(product.base_price || product.price || 0);
      const linkedRS = product.linked_revenue_stream_name || '-';
      
      // Handle per-year units or single base units
      const baseUnits = product.base_year_units || product.units || 0;
      const unitsDisplay = baseUnits.toLocaleString();
      
      // Handle growth rate - check if it's an array (per-year) or single value
      let growthDisplay = 'N/A';
      if (product.annual_sales_volume_growth_rates && Array.isArray(product.annual_sales_volume_growth_rates)) {
        // Per-year growth rates
        const rates = product.annual_sales_volume_growth_rates.slice(0, 3); // First 3 years
        growthDisplay = rates.map(r => `${Number(r).toFixed(1)}%`).join(', ');
      } else {
        // Single growth rate
        const growthRate = product.annual_sales_volume_growth || product.growth_rate || product.annual_growth_rate || 0;
        growthDisplay = `${Number(growthRate).toFixed(1)}%`;
      }
      
      return [name, sku, type, price, unitsDisplay, growthDisplay, linkedRS];
    });

    autoTable(pdf, {
      startY: yPos,
      head: [['Product/Service', 'SKU', 'Type', 'Price', 'Base Units', 'Growth Rate', 'Linked RS']],
      body: productsData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], fontSize: 8 },
      bodyStyles: { fontSize: 7 },
      margin: { left: 20, right: 20 },
      columnStyles: {
        0: { cellWidth: 35 },  // Product name - reduced
        1: { cellWidth: 18 },  // SKU - reduced
        2: { cellWidth: 18 },  // Type - reduced
        3: { cellWidth: 22 },  // Price - reduced
        4: { cellWidth: 18 },  // Base Units - reduced
        5: { cellWidth: 28 },  // Growth Rate
        6: { cellWidth: 30 }   // Linked RS - reduced
      }
    });

    yPos = pdf.lastAutoTable.finalY + 12;
    return yPos;
  };

  /**
   * Helper function to create chart on canvas and return as image
   */
  const createChartImage = async (chartConfig) => {
    return new Promise((resolve) => {
      // Create a temporary canvas
      const canvas = document.createElement('canvas');
      canvas.width = 800;
      canvas.height = 400;
      const ctx = canvas.getContext('2d');

      // Import Chart.js dynamically
      import('chart.js/auto').then((ChartJS) => {
        const Chart = ChartJS.default;
        
        // Create chart
        const chart = new Chart(ctx, chartConfig);
        
        // Wait for animation to complete
        setTimeout(() => {
          const imageData = canvas.toDataURL('image/png');
          chart.destroy();
          resolve(imageData);
        }, 500);
      });
    });
  };

  /**
   * Add Strategic Framework - Sales Analytics sub-section with full page replication
   */
  const addSFSalesAnalyticsSubSection = async (pdf, sfData, yPos) => {
    // Check if we need a new page
    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }

    pdf.setFontSize(16);
    pdf.setTextColor(31, 41, 55);
    pdf.text('Sales Analytics & Forecasting', 20, yPos);
    yPos += 10;

    setProgress('Fetching comprehensive sales analytics...');

    try {
      const analyticsData = await fetchSectionData('sales_analytics');
      
      if (!analyticsData || analyticsData.error) {
        pdf.setFontSize(10);
        pdf.setTextColor(239, 68, 68);
        pdf.text('Error fetching sales analytics data', 20, yPos);
        yPos += 10;
        return yPos;
      }
      
      const { kpi_metrics, forecasting_table, portfolio_totals, charts, insights } = analyticsData;
      
      if (!forecasting_table || forecasting_table.length === 0) {
        pdf.setFontSize(10);
        pdf.setTextColor(107, 114, 128);
        pdf.text('No sales data available yet. Create products with growth rates first.', 20, yPos);
        yPos += 10;
        return yPos;
      }

      // 1. KPI METRICS SECTION (4 cards)
      pdf.setFontSize(12);
      pdf.setTextColor(59, 130, 246);
      pdf.text('Sales Overview Metrics', 20, yPos);
      yPos += 8;

      const metrics = [
        { label: 'Total Products', value: `${kpi_metrics.total_products} (${kpi_metrics.products_count}P / ${kpi_metrics.services_count}S)` },
        { label: '2025 Revenue', value: formatCurrencyForPDF(kpi_metrics.revenue_2025) },
        { label: '2029 Revenue', value: formatCurrencyForPDF(kpi_metrics.revenue_2029) },
        { label: 'Avg Growth', value: `${kpi_metrics.avg_growth_rate.toFixed(1)}%` }
      ];

      metrics.forEach((metric, idx) => {
        const xPos = 20 + (idx * 47);
        pdf.setFontSize(8);
        pdf.setTextColor(107, 114, 128);
        pdf.text(metric.label, xPos, yPos);
        pdf.setFontSize(11);
        pdf.setTextColor(31, 41, 55);
        pdf.text(metric.value, xPos, yPos + 5);
      });
      yPos += 15;

      // 2. FORECASTING TABLE
      if (yPos > 200) {
        pdf.addPage();
        yPos = 20;
      }

      pdf.setFontSize(12);
      pdf.setTextColor(59, 130, 246);
      pdf.text('Sales Forecasting Matrix', 20, yPos);
      yPos += 7;

      // Table with all products and yearly projections - COMPLETE VERSION
      const tableData = forecasting_table.map(product => {
        const row = [
          product.name.substring(0, 18),
          product.type === 'services' ? 'Service' : 'Product',
          formatCurrencyForPDF(product.base_price),
          product.growth_type === 'per_year' ? 'Per Year' : `${product.growth_rate.toFixed(1)}%`,
          product.base_units.toLocaleString()
        ];
        
        // Add yearly data (units/revenue for each year)
        product.yearly_projections.forEach(year => {
          row.push(`${year.units.toLocaleString()}\n${formatCurrencyForPDF(year.revenue)}`);
        });
        
        return row;
      });

      // Add portfolio totals row
      const totalsRow = ['Portfolio Totals', '-', '-', '-', '-'];
      portfolio_totals.forEach(year => {
        totalsRow.push(`${year.units.toLocaleString()}\n${formatCurrencyForPDF(year.revenue)}`);
      });
      tableData.push(totalsRow);

      const years = portfolio_totals.map(y => y.year);
      autoTable(pdf, {
        startY: yPos,
        head: [['Product', 'Type', 'Price', 'Growth %', 'Base Units', ...years.map(y => y.toString())]],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246], fontSize: 6 },
        bodyStyles: { fontSize: 5, cellPadding: 1.5 },
        margin: { left: 10, right: 10 },
        columnStyles: {
          0: { cellWidth: 28 },
          1: { cellWidth: 15 },
          2: { cellWidth: 18 },
          3: { cellWidth: 16 },
          4: { cellWidth: 16 },
          // Yearly columns
          5: { cellWidth: 21 },
          6: { cellWidth: 21 },
          7: { cellWidth: 21 },
          8: { cellWidth: 21 },
          9: { cellWidth: 21 }
        },
        didParseCell: function(data) {
          if (data.row.index === tableData.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [226, 232, 240];
          }
        }
      });

      yPos = pdf.lastAutoTable.finalY + 12;

      // Add new page for charts
      pdf.addPage();
      yPos = 20;

      // 3. CHARTS SECTION - Render actual visual charts
      pdf.setFontSize(14);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Charts & Analytics', 20, yPos);
      yPos += 10;

      // Chart 1: Portfolio Revenue Trend (Line Chart)
      setProgress('Rendering Portfolio Revenue Trend chart...');
      pdf.setFontSize(11);
      pdf.setTextColor(59, 130, 246);
      pdf.text('Portfolio Revenue Trend (5 Years)', 20, yPos);
      yPos += 6;

      const lineChartConfig = {
        type: 'line',
        data: {
          labels: charts.revenue_trend.map(d => d.year),
          datasets: [{
            label: 'Revenue (Millions)',
            data: charts.revenue_trend.map(d => d.revenue),
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderWidth: 3,
            tension: 0.4,
            fill: true
          }]
        },
        options: {
          responsive: false,
          plugins: {
            legend: { display: true, position: 'top' },
            title: { display: false }
          },
          scales: {
            y: {
              beginAtZero: true,
              title: { display: true, text: 'Revenue (M)' }
            },
            x: {
              title: { display: true, text: 'Year' }
            }
          }
        }
      };

      const lineChartImage = await createChartImage(lineChartConfig);
      pdf.addImage(lineChartImage, 'PNG', 20, yPos, 170, 85);
      yPos += 95;

      // Chart 2: Revenue by Product (Bar Chart)
      if (yPos > 180) {
        pdf.addPage();
        yPos = 20;
      }

      setProgress('Rendering Revenue by Product chart...');
      pdf.setFontSize(11);
      pdf.setTextColor(59, 130, 246);
      pdf.text('Top 8 Products by 2029 Revenue', 20, yPos);
      yPos += 6;

      const barChartConfig = {
        type: 'bar',
        data: {
          labels: charts.revenue_by_product.map(d => d.name),
          datasets: [{
            label: 'Revenue (Thousands)',
            data: charts.revenue_by_product.map(d => d.revenue),
            backgroundColor: '#10b981',
            borderColor: '#059669',
            borderWidth: 1
          }]
        },
        options: {
          responsive: false,
          indexAxis: 'y',
          plugins: {
            legend: { display: false },
            title: { display: false }
          },
          scales: {
            x: {
              beginAtZero: true,
              title: { display: true, text: 'Revenue ($K)' }
            }
          }
        }
      };

      const barChartImage = await createChartImage(barChartConfig);
      pdf.addImage(barChartImage, 'PNG', 20, yPos, 170, 85);
      yPos += 95;

      // Add new page for remaining charts
      pdf.addPage();
      yPos = 20;

      // Chart 3: Units Projection (Area/Stacked Chart)
      setProgress('Rendering Units Projection chart...');
      pdf.setFontSize(11);
      pdf.setTextColor(59, 130, 246);
      pdf.text('Units Sold Projection (Products vs Services)', 20, yPos);
      yPos += 6;

      const areaChartConfig = {
        type: 'line',
        data: {
          labels: charts.units_projection.map(d => d.year),
          datasets: [
            {
              label: 'Products Units',
              data: charts.units_projection.map(d => d.productsUnits),
              borderColor: '#3b82f6',
              backgroundColor: 'rgba(59, 130, 246, 0.6)',
              fill: true,
              tension: 0.4
            },
            {
              label: 'Services Units',
              data: charts.units_projection.map(d => d.servicesUnits),
              borderColor: '#8b5cf6',
              backgroundColor: 'rgba(139, 92, 246, 0.6)',
              fill: true,
              tension: 0.4
            }
          ]
        },
        options: {
          responsive: false,
          plugins: {
            legend: { display: true, position: 'top' },
            title: { display: false }
          },
          scales: {
            y: {
              stacked: true,
              beginAtZero: true,
              title: { display: true, text: 'Units' }
            },
            x: {
              title: { display: true, text: 'Year' }
            }
          }
        }
      };

      const areaChartImage = await createChartImage(areaChartConfig);
      pdf.addImage(areaChartImage, 'PNG', 20, yPos, 170, 85);
      yPos += 95;

      // Chart 4: Growth Distribution (Bar Chart)
      setProgress('Rendering Growth Distribution chart...');
      pdf.setFontSize(11);
      pdf.setTextColor(59, 130, 246);
      pdf.text('Growth Rate Distribution', 20, yPos);
      yPos += 6;

      const growthChartConfig = {
        type: 'bar',
        data: {
          labels: charts.growth_distribution.map(d => d.range),
          datasets: [{
            label: 'Number of Products',
            data: charts.growth_distribution.map(d => d.count),
            backgroundColor: ['#ef4444', '#f97316', '#eab308', '#22c55e', '#10b981'],
            borderWidth: 1
          }]
        },
        options: {
          responsive: false,
          plugins: {
            legend: { display: false },
            title: { display: false }
          },
          scales: {
            y: {
              beginAtZero: true,
              title: { display: true, text: 'Count' },
              ticks: { stepSize: 1 }
            },
            x: {
              title: { display: true, text: 'Growth Range' }
            }
          }
        }
      };

      const growthChartImage = await createChartImage(growthChartConfig);
      pdf.addImage(growthChartImage, 'PNG', 20, yPos, 170, 85);
      yPos += 95;

      // Check if need new page
      if (yPos > 200) {
        pdf.addPage();
        yPos = 20;
      }

      // 4. KEY INSIGHTS SECTION
      pdf.setFontSize(13);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Key Insights Summary', 20, yPos);
      yPos += 8;

      const insightsData = [
        ['Portfolio CAGR', `${insights.portfolio_cagr.toFixed(1)}%`, '5-year compound annual growth rate'],
        ['Top Growth Product', insights.top_growth_product.name, `${insights.top_growth_product.growth_rate}% growth`],
        ['Average Price Point', formatCurrencyForPDF(insights.avg_price), 'Across all products/services'],
        ['Revenue Growth', formatCurrencyForPDF(insights.revenue_growth_5y), 'Expected increase (2025-2029)'],
        ['Portfolio Mix', `${insights.portfolio_mix.products_pct}% Products`, `${insights.portfolio_mix.services_pct}% Services`],
        ['Data Coverage', `${insights.data_coverage.coverage_pct}%`, `${insights.data_coverage.products_with_data}/${insights.data_coverage.total_products} have growth data`]
      ];

      autoTable(pdf, {
        startY: yPos,
        body: insightsData,
        theme: 'plain',
        bodyStyles: { fontSize: 9 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 50, textColor: [31, 41, 55] },
          1: { fontStyle: 'bold', cellWidth: 50, textColor: [59, 130, 246] },
          2: { cellWidth: 90, textColor: [107, 114, 128] }
        },
        margin: { left: 20, right: 20 }
      });

      yPos = pdf.lastAutoTable.finalY + 12;
      return yPos;

    } catch (error) {
      console.error('Error rendering sales analytics:', error);
      pdf.setFontSize(10);
      pdf.setTextColor(239, 68, 68);
      pdf.text('Error rendering sales analytics section', 20, yPos);
      yPos += 10;
      return yPos;
    }
  };

  /**
   * Add Cost Centers section to PDF
   */
  const addCostCentersSection = (pdf, data, yPos) => {
    // Check if we need a new page
    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }

    pdf.setFontSize(16);
    pdf.setTextColor(31, 41, 55);
    pdf.text('Cost Center Management', 20, yPos);
    yPos += 10;

    // Metrics
    pdf.setFontSize(11);
    pdf.setTextColor(75, 85, 99);
    
    const metrics = data.metrics || {};
    pdf.text(`Total Cost Centers: ${metrics.cost_centers_count || 0}`, 20, yPos);
    yPos += 7;
    pdf.text(`Total Annual Budget: ${formatCurrencyForPDF(metrics.total_annual_budget || 0)}`, 20, yPos);
    yPos += 7;
    pdf.text(`Average Monthly Budget: ${formatCurrencyForPDF(metrics.avg_monthly_budget || 0)}`, 20, yPos);
    yPos += 7;
    pdf.text(`Direct Costs: ${formatCurrencyForPDF(metrics.direct_costs || 0)}`, 20, yPos);
    yPos += 7;
    pdf.text(`Indirect Costs: ${formatCurrencyForPDF(metrics.indirect_costs || 0)}`, 20, yPos);
    yPos += 7;
    pdf.text(`Total Cost Items: ${metrics.total_cost_items || 0}`, 20, yPos);
    yPos += 12;

    // Cost Centers Table
    if (data.cost_centers && data.cost_centers.length > 0) {
      pdf.setFontSize(12);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Cost Centers', 20, yPos);
      yPos += 5;

      const costCenterData = data.cost_centers.slice(0, 15).map(cc => [
        cc.name || 'Unnamed',
        cc.type || '-',
        formatCurrencyForPDF(cc.annual_budget || 0),
        formatCurrencyForPDF(cc.direct_costs || 0),
        formatCurrencyForPDF(cc.indirect_costs || 0),
        cc.items_count || 0
      ]);

      autoTable(pdf, {
        startY: yPos,
        head: [['Cost Center', 'Type', 'Annual Budget', 'Direct', 'Indirect', 'Items']],
        body: costCenterData,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 8 }
      });

      yPos = pdf.lastAutoTable.finalY + 10;
    }

    // Budget by Type summary
    if (data.by_type && Object.keys(data.by_type).length > 0) {
      pdf.setFontSize(12);
      pdf.text('Budget by Type', 20, yPos);
      yPos += 5;

      const typeData = Object.entries(data.by_type).map(([type, info]) => [
        type.charAt(0).toUpperCase() + type.slice(1),
        info.count || 0,
        formatCurrencyForPDF(info.budget || 0)
      ]);

      autoTable(pdf, {
        startY: yPos,
        head: [['Type', 'Count', 'Total Budget']],
        body: typeData,
        theme: 'striped',
        headStyles: { fillColor: [107, 114, 128] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 9 }
      });

      yPos = pdf.lastAutoTable.finalY + 10;
    }

    return yPos;
  };

  /**
   * Add Financial Statements section to PDF
   */
  const addFinancialStatementsSection = (pdf, data, yPos) => {
    // Check if we need a new page
    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }

    pdf.setFontSize(16);
    pdf.setTextColor(31, 41, 55);
    pdf.text('Financial Statements (Projected)', 20, yPos);
    yPos += 10;

    // Metrics
    pdf.setFontSize(11);
    pdf.setTextColor(75, 85, 99);
    
    const metrics = data.metrics || {};
    const costBreakdown = data.cost_breakdown || {};
    
    pdf.text(`Total Revenue: ${formatCurrencyForPDF(metrics.total_revenue || 0)}`, 20, yPos);
    yPos += 7;
    pdf.text(`Total Net Income: ${formatCurrencyForPDF(metrics.total_net_income || 0)}`, 20, yPos);
    yPos += 7;
    pdf.text(`Average Gross Margin: ${(metrics.avg_gross_margin || 0).toFixed(1)}%`, 20, yPos);
    yPos += 7;
    pdf.text(`Average Operating Margin: ${(metrics.avg_operating_margin || 0).toFixed(1)}%`, 20, yPos);
    yPos += 7;
    pdf.text(`Average Net Margin: ${(metrics.avg_net_margin || 0).toFixed(1)}%`, 20, yPos);
    yPos += 7;
    pdf.text(`Data Source: ${costBreakdown.data_source === 'actual' ? 'Actual Cost Data' : 'Estimated'}`, 20, yPos);
    yPos += 12;

    // Cost breakdown summary
    if (costBreakdown.base_cogs || costBreakdown.base_opex) {
      pdf.setFontSize(12);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Base Year Cost Structure', 20, yPos);
      yPos += 5;

      const costData = [
        ['Base COGS', formatCurrencyForPDF(costBreakdown.base_cogs || 0)],
        ['Base Operating Expenses', formatCurrencyForPDF(costBreakdown.base_opex || 0)],
        ['Annual Interest Expense', formatCurrencyForPDF(costBreakdown.annual_interest || 0)]
      ];

      autoTable(pdf, {
        startY: yPos,
        head: [['Cost Type', 'Amount']],
        body: costData,
        theme: 'striped',
        headStyles: { fillColor: [239, 68, 68] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 9 }
      });

      yPos = pdf.lastAutoTable.finalY + 10;
    }

    // Yearly Data Table
    if (data.yearly_data && data.yearly_data.length > 0) {
      pdf.setFontSize(12);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Projected Income Statement', 20, yPos);
      yPos += 5;

      const yearlyTableData = data.yearly_data.map(year => [
        year.year.toString(),
        formatCurrencyForPDF(year.revenue || 0),
        formatCurrencyForPDF(year.cogs || 0),
        formatCurrencyForPDF(year.gross_profit || 0),
        formatCurrencyForPDF(year.operating_expenses || 0),
        formatCurrencyForPDF(year.net_income || 0),
        `${(year.net_margin || 0).toFixed(1)}%`
      ]);

      autoTable(pdf, {
        startY: yPos,
        head: [['Year', 'Revenue', 'COGS', 'Gross Profit', 'OpEx', 'Net Income', 'Margin']],
        body: yearlyTableData,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 8 }
      });

      yPos = pdf.lastAutoTable.finalY + 10;
    }

    return yPos;
  };

  /**
   * Add Financial Planning section to PDF
   */
  const addFinancialPlanningSection = (pdf, data, yPos) => {
    // Check if we need a new page
    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }

    pdf.setFontSize(16);
    pdf.setTextColor(31, 41, 55);
    pdf.text('Financial Planning', 20, yPos);
    yPos += 10;

    // Metrics
    pdf.setFontSize(11);
    pdf.setTextColor(75, 85, 99);
    
    const metrics = data.metrics || {};
    pdf.text(`Projected Revenue: ${formatCurrencyForPDF(metrics.projected_revenue || 0)}`, 20, yPos);
    yPos += 7;
    pdf.text(`Projected Units: ${(metrics.projected_units || 0).toLocaleString()}`, 20, yPos);
    yPos += 7;
    pdf.text(`Average Selling Price: ${formatCurrencyForPDF(metrics.avg_selling_price || 0)}`, 20, yPos);
    yPos += 7;
    pdf.text(`CAGR: ${(metrics.cagr || 0).toFixed(2)}%`, 20, yPos);
    yPos += 7;
    pdf.text(`Planning Period: ${metrics.base_year || 'N/A'} - ${(metrics.base_year || 0) + (metrics.planning_horizon || 0) - 1}`, 20, yPos);
    yPos += 12;

    // Yearly Totals Table (if available)
    if (data.yearly_totals && data.yearly_totals.length > 0) {
      pdf.setFontSize(12);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Revenue Projections by Year', 20, yPos);
      yPos += 5;

      const yearlyData = data.yearly_totals.map(y => [
        y.year,
        formatCurrencyForPDF(y.revenue || 0),
        (y.units || 0).toLocaleString()
      ]);

      autoTable(pdf, {
        startY: yPos,
        head: [['Year', 'Revenue', 'Units']],
        body: yearlyData,
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 9 }
      });

      yPos = pdf.lastAutoTable.finalY + 10;
    }

    // Product Projections Table
    if (data.product_projections && data.product_projections.length > 0) {
      pdf.setFontSize(12);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Product Projections', 20, yPos);
      yPos += 5;

      const productData = data.product_projections.slice(0, 10).map(product => [
        product.product_name || 'Unnamed',
        product.category || '-',
        formatCurrencyForPDF(product.projected_revenue || 0),
        (product.projected_units || 0).toLocaleString(),
        formatCurrencyForPDF(product.base_price || 0),
        `${(product.growth_rate || 0).toFixed(1)}%`
      ]);

      autoTable(pdf, {
        startY: yPos,
        head: [['Product', 'Category', 'Revenue', 'Units', 'Price', 'Growth']],
        body: productData,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 8 }
      });

      yPos = pdf.lastAutoTable.finalY + 10;
    }

    return yPos;
  };

  /**
   * Add Cost Management Section to PDF
   */
  const addCostManagementSection = (pdf, data, yPos, subSectionId) => {
    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }

    pdf.setFontSize(14);
    pdf.setTextColor(31, 41, 55);
    
    if (subSectionId === 'cogs') {
      pdf.text('Cost of Goods Sold (COGS)', 20, yPos);
      yPos += 8;
      
      const cogs = data.cogs || {};
      pdf.setFontSize(11);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`Total COGS: ${formatCurrencyForPDF(cogs.total || 0)}`, 20, yPos);
      yPos += 10;
      
      if (cogs.by_product && cogs.by_product.length > 0) {
        const cogsData = cogs.by_product.slice(0, 10).map(p => [
          p.product_name || 'Unknown',
          p.sku || '-',
          `${(p.cogs_percentage || 0).toFixed(1)}%`,
          formatCurrencyForPDF(p.cogs_per_unit || 0),
          formatCurrencyForPDF(p.total_cogs || 0)
        ]);
        
        autoTable(pdf, {
          startY: yPos,
          head: [['Product', 'SKU', 'COGS %', 'Per Unit', 'Total']],
          body: cogsData,
          theme: 'striped',
          headStyles: { fillColor: [239, 68, 68] },
          margin: { left: 20, right: 20 },
          styles: { fontSize: 9 }
        });
        yPos = pdf.lastAutoTable.finalY + 10;
      }
    } else if (subSectionId === 'opex') {
      pdf.text('Operating Expenses', 20, yPos);
      yPos += 8;
      
      const opex = data.opex || {};
      pdf.setFontSize(11);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`Total Operating Expenses: ${formatCurrencyForPDF(opex.total || 0)}`, 20, yPos);
      yPos += 10;
      
      if (opex.by_category) {
        const opexData = Object.entries(opex.by_category).map(([category, info]) => [
          category,
          info.count || 0,
          formatCurrencyForPDF(info.amount || 0)
        ]);
        
        autoTable(pdf, {
          startY: yPos,
          head: [['Category', 'Items', 'Amount']],
          body: opexData,
          theme: 'striped',
          headStyles: { fillColor: [59, 130, 246] },
          margin: { left: 20, right: 20 },
          styles: { fontSize: 9 }
        });
        yPos = pdf.lastAutoTable.finalY + 10;
      }
    } else if (subSectionId === 'fixed_variable') {
      pdf.text('Cost Structure: Fixed vs Variable', 20, yPos);
      yPos += 8;
      
      const structure = data.cost_structure || {};
      const tableData = [
        ['Fixed Costs', formatCurrencyForPDF(structure.fixed_costs?.total || 0), `${(structure.fixed_costs?.percentage || 0).toFixed(1)}%`],
        ['Variable Costs', formatCurrencyForPDF(structure.variable_costs?.total || 0), `${(structure.variable_costs?.percentage || 0).toFixed(1)}%`],
        ['Semi-Variable', formatCurrencyForPDF(structure.semi_variable_costs?.total || 0), `${(structure.semi_variable_costs?.percentage || 0).toFixed(1)}%`]
      ];
      
      autoTable(pdf, {
        startY: yPos,
        head: [['Cost Type', 'Amount', '% of Total']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 10 }
      });
      yPos = pdf.lastAutoTable.finalY + 10;
    } else if (subSectionId === 'margin_analysis') {
      pdf.text('Margin Analysis', 20, yPos);
      yPos += 8;
      
      const margins = data.margins || {};
      pdf.setFontSize(11);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`Gross Margin: ${(margins.gross_margin || 0).toFixed(2)}%`, 20, yPos);
      yPos += 7;
      pdf.text(`Operating Margin: ${(margins.operating_margin || 0).toFixed(2)}%`, 20, yPos);
      yPos += 7;
      pdf.text(`Total Revenue: ${formatCurrencyForPDF(margins.total_revenue || 0)}`, 20, yPos);
      yPos += 7;
      pdf.text(`Total COGS: ${formatCurrencyForPDF(margins.total_cogs || 0)}`, 20, yPos);
      yPos += 7;
      pdf.text(`Total OpEx: ${formatCurrencyForPDF(margins.total_opex || 0)}`, 20, yPos);
      yPos += 10;
    }
    
    return yPos;
  };

  /**
   * Add Financial Management Section to PDF
   */
  const addFinancialManagementSection = (pdf, data, yPos, subSectionId) => {
    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }

    pdf.setFontSize(14);
    pdf.setTextColor(31, 41, 55);
    
    if (subSectionId === 'working_capital') {
      pdf.text('Working Capital', 20, yPos);
      yPos += 8;
      
      const wc = data.working_capital || {};
      pdf.setFontSize(11);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`Net Working Capital: ${formatCurrencyForPDF(wc.net_working_capital || 0)}`, 20, yPos);
      yPos += 7;
      pdf.text(`Current Ratio: ${(wc.current_ratio || 0).toFixed(2)}`, 20, yPos);
      yPos += 10;
      
      const tableData = [
        ['Cash', formatCurrencyForPDF(wc.current_assets?.cash || 0)],
        ['Accounts Receivable', formatCurrencyForPDF(wc.current_assets?.accounts_receivable || 0)],
        ['Inventory', formatCurrencyForPDF(wc.current_assets?.inventory || 0)],
        ['Total Current Assets', formatCurrencyForPDF(wc.current_assets?.total || 0)],
        ['Accounts Payable', formatCurrencyForPDF(wc.current_liabilities?.accounts_payable || 0)],
        ['Short-term Debt', formatCurrencyForPDF(wc.current_liabilities?.short_term_debt || 0)],
        ['Total Current Liabilities', formatCurrencyForPDF(wc.current_liabilities?.total || 0)]
      ];
      
      autoTable(pdf, {
        startY: yPos,
        head: [['Item', 'Amount']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 9 }
      });
      yPos = pdf.lastAutoTable.finalY + 10;
    } else if (subSectionId === 'assets') {
      pdf.text('Assets', 20, yPos);
      yPos += 8;
      
      const assets = data.assets || {};
      const tableData = [
        ['Current Assets', formatCurrencyForPDF(assets.current_assets || 0)],
        ['Fixed Assets', formatCurrencyForPDF(assets.fixed_assets || 0)],
        ['Intangible Assets', formatCurrencyForPDF(assets.intangible_assets || 0)],
        ['Total Assets', formatCurrencyForPDF(assets.total_assets || 0)]
      ];
      
      autoTable(pdf, {
        startY: yPos,
        head: [['Asset Type', 'Amount']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 10 }
      });
      yPos = pdf.lastAutoTable.finalY + 10;
    } else if (subSectionId === 'liabilities') {
      pdf.text('Liabilities', 20, yPos);
      yPos += 8;
      
      const liabilities = data.liabilities || {};
      const tableData = [
        ['Current Liabilities', formatCurrencyForPDF(liabilities.current_liabilities || 0)],
        ['Long-term Debt', formatCurrencyForPDF(liabilities.long_term_debt || 0)],
        ['Total Liabilities', formatCurrencyForPDF(liabilities.total_liabilities || 0)]
      ];
      
      autoTable(pdf, {
        startY: yPos,
        head: [['Liability Type', 'Amount']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [239, 68, 68] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 10 }
      });
      yPos = pdf.lastAutoTable.finalY + 10;
      
      // Loans table
      if (liabilities.loans && liabilities.loans.length > 0) {
        pdf.setFontSize(12);
        pdf.text('Loans Detail', 20, yPos);
        yPos += 5;
        
        const loansData = liabilities.loans.map(l => [
          l.name || 'Unnamed',
          formatCurrencyForPDF(l.principal || l.current_balance || 0),
          `${(l.interest_rate || 0).toFixed(2)}%`,
          l.term_months ? `${l.term_months} mo` : `${l.term_years || 0} yrs`,
          formatCurrencyForPDF(l.monthly_payment || 0)
        ]);
        
        autoTable(pdf, {
          startY: yPos,
          head: [['Loan', 'Principal', 'Interest', 'Term', 'Monthly Payment']],
          body: loansData,
          theme: 'striped',
          headStyles: { fillColor: [107, 114, 128] },
          margin: { left: 20, right: 20 },
          styles: { fontSize: 9 }
        });
        yPos = pdf.lastAutoTable.finalY + 10;
      }
    } else if (subSectionId === 'equity') {
      pdf.text('Equity Structure', 20, yPos);
      yPos += 8;
      
      const equity = data.equity || {};
      pdf.setFontSize(11);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`Total Equity: ${formatCurrencyForPDF(equity.total_equity || 0)}`, 20, yPos);
      yPos += 7;
      pdf.text(`Contributed Capital: ${formatCurrencyForPDF(equity.contributed_capital || 0)}`, 20, yPos);
      yPos += 7;
      pdf.text(`Retained Earnings: ${formatCurrencyForPDF(equity.retained_earnings || 0)}`, 20, yPos);
      yPos += 10;
      
      if (equity.entries && equity.entries.length > 0) {
        const equityData = equity.entries.map(e => [
          e.type || 'Unknown',
          e.investor || '-',
          formatCurrencyForPDF(e.amount || 0)
        ]);
        
        autoTable(pdf, {
          startY: yPos,
          head: [['Type', 'Investor', 'Amount']],
          body: equityData,
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246] },
          margin: { left: 20, right: 20 },
          styles: { fontSize: 9 }
        });
        yPos = pdf.lastAutoTable.finalY + 10;
      }
    }
    
    return yPos;
  };

  /**
   * Add Financial Analytics Section to PDF
   */
  const addFinancialAnalyticsSection = (pdf, data, yPos, subSectionId) => {
    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }

    pdf.setFontSize(14);
    pdf.setTextColor(31, 41, 55);
    
    if (subSectionId === 'profitability') {
      pdf.text('Profitability Ratios', 20, yPos);
      yPos += 8;
      
      const prof = data.profitability || {};
      const tableData = [
        ['Gross Margin', `${(prof.gross_margin || 0).toFixed(2)}%`],
        ['Operating Margin', `${(prof.operating_margin || 0).toFixed(2)}%`],
        ['Net Margin', `${(prof.net_margin || 0).toFixed(2)}%`],
        ['ROI', `${(prof.roi || 0).toFixed(2)}%`],
        ['ROE', `${(prof.roe || 0).toFixed(2)}%`],
        ['Gross Profit', formatCurrencyForPDF(prof.gross_profit || 0)],
        ['Operating Income', formatCurrencyForPDF(prof.operating_income || 0)],
        ['Net Income', formatCurrencyForPDF(prof.net_income || 0)]
      ];
      
      autoTable(pdf, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 10 }
      });
      yPos = pdf.lastAutoTable.finalY + 10;
    } else if (subSectionId === 'liquidity') {
      pdf.text('Liquidity Ratios', 20, yPos);
      yPos += 8;
      
      const liq = data.liquidity || {};
      const tableData = [
        ['Current Ratio', (liq.current_ratio || 0).toFixed(2)],
        ['Quick Ratio', (liq.quick_ratio || 0).toFixed(2)],
        ['Cash Ratio', (liq.cash_ratio || 0).toFixed(2)],
        ['Working Capital', formatCurrencyForPDF(liq.working_capital || 0)]
      ];
      
      autoTable(pdf, {
        startY: yPos,
        head: [['Ratio', 'Value']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 10 }
      });
      yPos = pdf.lastAutoTable.finalY + 10;
    } else if (subSectionId === 'efficiency') {
      pdf.text('Efficiency Ratios', 20, yPos);
      yPos += 8;
      
      const eff = data.efficiency || {};
      const tableData = [
        ['Asset Turnover', (eff.asset_turnover || 0).toFixed(2)],
        ['Inventory Turnover', (eff.inventory_turnover || 0).toFixed(2)],
        ['Receivables Turnover', (eff.receivables_turnover || 0).toFixed(2)]
      ];
      
      autoTable(pdf, {
        startY: yPos,
        head: [['Ratio', 'Value']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [245, 158, 11] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 10 }
      });
      yPos = pdf.lastAutoTable.finalY + 10;
    } else if (subSectionId === 'growth_metrics') {
      pdf.text('Growth Metrics', 20, yPos);
      yPos += 8;
      
      const growth = data.growth || {};
      pdf.setFontSize(11);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`Revenue Growth Rate: ${(growth.revenue_growth || 0).toFixed(1)}%`, 20, yPos);
      yPos += 7;
      pdf.text(`Projected CAGR: ${(growth.projected_cagr || 0).toFixed(1)}%`, 20, yPos);
      yPos += 10;
      
      if (growth.yoy_revenue && growth.yoy_revenue.length > 0) {
        const yoyData = growth.yoy_revenue.map(y => [
          y.year,
          formatCurrencyForPDF(y.revenue || 0)
        ]);
        
        autoTable(pdf, {
          startY: yPos,
          head: [['Year', 'Projected Revenue']],
          body: yoyData,
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246] },
          margin: { left: 20, right: 20 },
          styles: { fontSize: 10 }
        });
        yPos = pdf.lastAutoTable.finalY + 10;
      }
    }
    
    return yPos;
  };

  /**
   * Add Planning Dashboard Section to PDF
   */
  const addPlanningDashboardSection = (pdf, data, yPos, subSectionId) => {
    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }

    pdf.setFontSize(14);
    pdf.setTextColor(31, 41, 55);
    
    if (subSectionId === 'executive_summary') {
      pdf.text('Executive Summary', 20, yPos);
      yPos += 8;
      
      const summary = data.executive_summary || {};
      pdf.setFontSize(11);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`Plan: ${summary.plan_name || 'N/A'}`, 20, yPos);
      yPos += 7;
      pdf.text(`Company: ${summary.company_name || 'N/A'}`, 20, yPos);
      yPos += 7;
      pdf.text(`Industry: ${summary.industry || 'N/A'}`, 20, yPos);
      yPos += 7;
      pdf.text(`Planning Horizon: ${summary.planning_horizon || 3} years`, 20, yPos);
      yPos += 10;
      
      const tableData = [
        ['Total Revenue', formatCurrencyForPDF(summary.total_revenue || 0)],
        ['Gross Profit', formatCurrencyForPDF(summary.gross_profit || 0)],
        ['Operating Profit', formatCurrencyForPDF(summary.operating_profit || 0)],
        ['Gross Margin', `${(summary.gross_margin || 0).toFixed(2)}%`],
        ['Operating Margin', `${(summary.operating_margin || 0).toFixed(2)}%`]
      ];
      
      autoTable(pdf, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 10 }
      });
      yPos = pdf.lastAutoTable.finalY + 10;
    } else if (subSectionId === 'kpis') {
      pdf.text('Key Performance Indicators', 20, yPos);
      yPos += 8;
      
      const kpis = data.kpis || {};
      const tableData = [
        ['Revenue', formatCurrencyForPDF(kpis.revenue?.current || 0), formatCurrencyForPDF(kpis.revenue?.target || 0)],
        ['Gross Margin', `${(kpis.gross_margin?.current || 0).toFixed(2)}%`, `${kpis.gross_margin?.target || 0}%`],
        ['Operating Margin', `${(kpis.operating_margin?.current || 0).toFixed(2)}%`, `${kpis.operating_margin?.target || 0}%`]
      ];
      
      autoTable(pdf, {
        startY: yPos,
        head: [['KPI', 'Current', 'Target']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 10 }
      });
      yPos = pdf.lastAutoTable.finalY + 10;
    } else if (subSectionId === 'performance') {
      pdf.text('Performance Indicators', 20, yPos);
      yPos += 8;
      
      const perf = data.performance || {};
      const sf = perf.strategic_framework || {};
      
      pdf.setFontSize(11);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`Strategic Objectives: ${sf.objectives || 0}`, 20, yPos);
      yPos += 7;
      pdf.text(`Initiatives: ${sf.initiatives || 0}`, 20, yPos);
      yPos += 7;
      pdf.text(`Revenue Streams: ${sf.revenue_streams || 0}`, 20, yPos);
      yPos += 7;
      pdf.text(`Products: ${sf.products || 0}`, 20, yPos);
      yPos += 10;
      
      // Top Products
      if (perf.top_products && perf.top_products.length > 0) {
        pdf.setFontSize(12);
        pdf.text('Top Products by Revenue', 20, yPos);
        yPos += 5;
        
        const productData = perf.top_products.map(p => [
          p.name || 'Unknown',
          formatCurrencyForPDF(p.revenue || 0)
        ]);
        
        autoTable(pdf, {
          startY: yPos,
          head: [['Product', 'Revenue']],
          body: productData,
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246] },
          margin: { left: 20, right: 20 },
          styles: { fontSize: 9 }
        });
        yPos = pdf.lastAutoTable.finalY + 10;
      }
    }
    
    return yPos;
  };

  /**
   * Add Scenario Planning Section to PDF
   */
  const addScenarioPlanningSection = (pdf, data, yPos, subSectionId) => {
    if (yPos > 250) {
      pdf.addPage();
      yPos = 20;
    }

    pdf.setFontSize(14);
    pdf.setTextColor(31, 41, 55);
    
    const renderScenario = (scenario, title, color) => {
      pdf.text(title, 20, yPos);
      yPos += 8;
      
      const tableData = [
        ['Revenue', formatCurrencyForPDF(scenario.revenue || 0)],
        ['Costs', formatCurrencyForPDF(scenario.costs || 0)],
        ['Profit', formatCurrencyForPDF(scenario.profit || 0)],
        ['Margin', `${(scenario.margin || 0).toFixed(2)}%`],
        ['Growth Rate', `${(scenario.growth_rate || 0).toFixed(1)}%`]
      ];
      
      autoTable(pdf, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: color },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 10 }
      });
      yPos = pdf.lastAutoTable.finalY + 10;
      
      return yPos;
    };
    
    if (subSectionId === 'base_case') {
      yPos = renderScenario(data.base_case || {}, 'Base Case Scenario', [59, 130, 246]);
    } else if (subSectionId === 'optimistic') {
      yPos = renderScenario(data.optimistic || {}, 'Optimistic Scenario', [16, 185, 129]);
    } else if (subSectionId === 'pessimistic') {
      yPos = renderScenario(data.pessimistic || {}, 'Pessimistic Scenario', [239, 68, 68]);
    } else if (subSectionId === 'sensitivity') {
      pdf.text('Sensitivity Analysis', 20, yPos);
      yPos += 8;
      
      const sensitivity = data.sensitivity || {};
      
      if (sensitivity.revenue_impact) {
        pdf.setFontSize(12);
        pdf.text('Revenue Impact on Profit', 20, yPos);
        yPos += 5;
        
        const impactData = sensitivity.revenue_impact.map(i => [
          i.change,
          formatCurrencyForPDF(i.profit_impact || 0)
        ]);
        
        autoTable(pdf, {
          startY: yPos,
          head: [['Revenue Change', 'Profit Impact']],
          body: impactData,
          theme: 'striped',
          headStyles: { fillColor: [107, 114, 128] },
          margin: { left: 20, right: 20 },
          styles: { fontSize: 9 }
        });
        yPos = pdf.lastAutoTable.finalY + 10;
      }
      
      if (sensitivity.breakeven) {
        pdf.setFontSize(11);
        pdf.setTextColor(75, 85, 99);
        pdf.text(`Break-even Units: ${(sensitivity.breakeven.units || 0).toLocaleString()}`, 20, yPos);
        yPos += 7;
        pdf.text(`Break-even Revenue: ${formatCurrencyForPDF(sensitivity.breakeven.revenue || 0)}`, 20, yPos);
        yPos += 10;
      }
    }
    
    return yPos;
  };

  /**
   * Get sub-section display name
   */
  const getSubSectionName = (subSectionId) => {
    const nameMap = {
      // Strategic Framework
      'objectives': 'Objectives',
      'initiatives': 'Initiatives',
      'revenue_streams': 'Revenue Streams',
      'products': 'Products & Services',
      'sales_analytics': 'Sales Analytics',
      // Cost Centers
      'overview': 'Cost Center Overview',
      'allocations': 'Budget Allocations',
      'cost_breakdown': 'Direct vs Indirect Costs',
      // Cost Management
      'cogs': 'COGS',
      'opex': 'Operating Expenses',
      'fixed_variable': 'Fixed vs Variable Costs',
      'cost_per_unit': 'Cost per Unit Analysis',
      'margin_analysis': 'Margin Analysis',
      // Financial Management
      'working_capital': 'Working Capital',
      'assets': 'Assets',
      'liabilities': 'Liabilities',
      'equity': 'Equity Structure',
      // Financial Planning
      'revenue_projections': 'Revenue Projections',
      'unit_projections': 'Unit Sales Projections',
      'pricing': 'Pricing Strategy',
      'growth': 'Growth Assumptions',
      'market_penetration': 'Market Penetration',
      // Financial Statements
      'income_statement': 'Income Statement',
      'balance_sheet': 'Balance Sheet',
      'cash_flow': 'Cash Flow Statement',
      'ratios': 'Key Financial Ratios',
      // Financial Analytics
      'profitability': 'Profitability Analysis',
      'liquidity': 'Liquidity Analysis',
      'efficiency': 'Efficiency Ratios',
      'growth_metrics': 'Growth Metrics',
      // Planning Dashboard
      'executive_summary': 'Executive Summary',
      'kpis': 'Key Performance Indicators',
      'performance': 'Performance Indicators',
      // Scenario Planning
      'base_case': 'Base Case',
      'optimistic': 'Optimistic Scenario',
      'pessimistic': 'Pessimistic Scenario',
      'sensitivity': 'Sensitivity Analysis'
    };
    return nameMap[subSectionId] || subSectionId;
  };

  /**
   * Generate comprehensive PDF with sub-sections
   */
  const generatePDF = async () => {
    setIsExporting(true);
    setExportError(null);
    setProgress('Initializing...');

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Parse selected sections
      const structuredSections = parseSelectedSections();
      
      // Cover Page
      setProgress('Creating cover page...');
      pdf.setFontSize(28);
      pdf.setTextColor(31, 41, 55);
      pdf.text(planName || 'Business Plan Export', 105, 60, { align: 'center' });
      
      pdf.setFontSize(14);
      pdf.setTextColor(75, 85, 99);
      pdf.text(currentPlan?.company_name || '', 105, 75, { align: 'center' });
      
      pdf.setFontSize(11);
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, 105, 90, { align: 'center' });
      pdf.text(`Currency: ${currentPlan?.currency || 'USD'}`, 105, 100, { align: 'center' });
      
      // Table of Contents
      pdf.setFontSize(16);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Sections Included:', 20, 120);
      pdf.setFontSize(10);
      pdf.setTextColor(75, 85, 99);
      let tocY = 130;
      
      Object.entries(structuredSections).forEach(([sectionId, subSections]) => {
        pdf.setFontSize(11);
        pdf.setTextColor(31, 41, 55);
        pdf.text(`${getSectionName(sectionId)}`, 25, tocY);
        tocY += 6;
        
        pdf.setFontSize(9);
        pdf.setTextColor(107, 114, 128);
        subSections.forEach(subId => {
          pdf.text(`• ${getSubSectionName(subId)}`, 30, tocY);
          tocY += 5;
        });
        tocY += 3;
      });

      // Fetch data and add sections with sub-sections
      pdf.addPage();
      let yPos = 20;

      for (const [sectionId, subSections] of Object.entries(structuredSections)) {
        setProgress(`Processing ${getSectionName(sectionId)}...`);

        // Add section header
        if (yPos > 270) {
          pdf.addPage();
          yPos = 20;
        }
        
        pdf.setFontSize(18);
        pdf.setTextColor(31, 41, 55);
        pdf.text(getSectionName(sectionId), 20, yPos);
        yPos += 12;

        // Fetch section data
        const sectionData = await fetchSectionData(sectionId);
        
        if (sectionData && !sectionData.error) {
          // Process each sub-section
          for (const subId of subSections) {
            setProgress(`Processing ${getSectionName(sectionId)} - ${getSubSectionName(subId)}...`);

            // Strategic Framework sub-sections
            if (sectionId === 'strategic_framework') {
              if (subId === 'objectives') {
                yPos = addSFObjectivesSubSection(pdf, sectionData, yPos);
              } else if (subId === 'initiatives') {
                yPos = addSFInitiativesSubSection(pdf, sectionData, yPos);
              } else if (subId === 'revenue_streams') {
                yPos = addSFRevenueStreamsSubSection(pdf, sectionData, yPos);
              } else if (subId === 'products') {
                yPos = addSFProductsSubSection(pdf, sectionData, yPos);
              } else if (subId === 'sales_analytics') {
                yPos = await addSFSalesAnalyticsSubSection(pdf, sectionData, yPos);
              }
            } 
            // Cost Centers sub-sections
            else if (sectionId === 'cost_centers') {
              yPos = addCostCentersSection(pdf, sectionData, yPos);
            }
            // Cost Management sub-sections
            else if (sectionId === 'cost_management') {
              yPos = addCostManagementSection(pdf, sectionData, yPos, subId);
            }
            // Financial Management sub-sections
            else if (sectionId === 'financial_management') {
              yPos = addFinancialManagementSection(pdf, sectionData, yPos, subId);
            }
            // Financial Statements sub-sections
            else if (sectionId === 'financial_statements') {
              yPos = addFinancialStatementsSection(pdf, sectionData, yPos);
            } 
            // Financial Planning sub-sections
            else if (sectionId === 'financial_planning') {
              yPos = addFinancialPlanningSection(pdf, sectionData, yPos);
            }
            // Financial Analytics sub-sections
            else if (sectionId === 'financial_analytics') {
              yPos = addFinancialAnalyticsSection(pdf, sectionData, yPos, subId);
            }
            // Planning Dashboard sub-sections
            else if (sectionId === 'planning_dashboard') {
              yPos = addPlanningDashboardSection(pdf, sectionData, yPos, subId);
            }
            // Scenario Planning sub-sections
            else if (sectionId === 'scenario_planning') {
              yPos = addScenarioPlanningSection(pdf, sectionData, yPos, subId);
            }
            // Placeholder for any remaining sections
            else {
              if (yPos > 250) {
                pdf.addPage();
                yPos = 20;
              }
              pdf.setFontSize(12);
              pdf.setTextColor(31, 41, 55);
              pdf.text(getSubSectionName(subId), 20, yPos);
              yPos += 8;
              pdf.setFontSize(10);
              pdf.setTextColor(107, 114, 128);
              pdf.text('Data export for this sub-section coming in next phase.', 20, yPos);
              yPos += 12;
            }
          }
        } else {
          // Section data not available
          pdf.setFontSize(11);
          pdf.setTextColor(239, 68, 68);
          pdf.text('Unable to fetch section data', 20, yPos);
          yPos += 15;
        }

        // Add spacing between sections
        yPos += 10;
      }

      // Save PDF
      setProgress('Saving PDF...');
      const fileName = `${planName || 'BusinessPlan'}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      setIsExporting(false);
      setProgress('');
      if (onComplete) onComplete();
    } catch (error) {
      console.error('Error generating PDF:', error);
      setExportError(error.message);
      setIsExporting(false);
      setProgress('');
    }
  };

  return (
    <div>
      {exportError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">Error: {exportError}</p>
        </div>
      )}

      {progress && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 text-sm">{progress}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={generatePDF}
          disabled={isExporting}
          className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 flex items-center gap-2"
        >
          {isExporting ? (
            <>
              <Loader className="h-5 w-5 animate-spin" />
              Generating PDF...
            </>
          ) : (
            <>
              <Download className="h-5 w-5" />
              Export to PDF
            </>
          )}
        </button>
        <button
          disabled
          className="px-6 py-3 bg-gray-300 text-gray-600 rounded-lg cursor-not-allowed flex items-center gap-2"
          title="Excel export coming in next phase"
        >
          <Download className="h-5 w-5" />
          Export to Excel (Next Phase)
        </button>
      </div>

      <p className="text-xs text-gray-500 mt-3">
        PDF includes metrics, tables, and data from each selected section.
      </p>
    </div>
  );
};

export default ComponentExporter;
